import type { StyleRule } from '@archipro-design/aria';
import { pxArrayToRem } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import {
    accordionClassName,
    accordionTitleSlotClassNames,
    accordionTitleClassName,
    accordionContentClassName,
    boxClassName,
} from '@fluentui/react-northstar';

export const FaqSectionWrapper: StyleRule = ({ theme }) => ({
    padding: pxArrayToRem([0, 18, 60, 18]),

    marginTop: 0,
    [theme.screen.laptop]: {
        barderTop: `none`,
        borderBottom: `${pxToRem(2)} solid #343434`,
    },

    [`& > .${accordionClassName}`]: {
        borderBottom: `none`,
    },
    [theme.screen.laptop]: {
        paddingBottom: pxToRem(80),
        paddingRight: pxToRem(50),
        paddingLeft: pxToRem(50),
        marginTop: pxToRem(96),
    },
    [`& div.${accordionTitleClassName}`]: {
        paddingTop: pxToRem(16),
        paddingBottom: pxToRem(16),
        paddingRight: pxToRem(6),
        borderTop: `${pxToRem(2)} solid ${
            theme.siteVariables.colorScheme.default?.foreground
        }`,
        [theme.screen.laptop]: {
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: 0,
        },
    },
    [`& div.${accordionTitleClassName}:last-child`]: {
        [theme.screen.laptop]: {
            borderBottom: `${pxToRem(2)} solid ${
                theme.siteVariables.colorScheme.default?.foreground
            }`,
        },
    },
    [`& div.${accordionContentClassName}`]: {
        fontSize: pxToRem(14),
        lineHeight: pxToRem(17.5),
        marginTop: 0,
        color: theme.siteVariables.colorScheme.default?.foreground,
        '& a': {
            color: theme.siteVariables.colorScheme.default?.foreground,
            textDecoration: 'underline',
        },
        '& a:link': {
            color: theme.siteVariables.colorScheme.default?.foreground,
        },
        marginBottom: pxToRem(0),
        [theme.screen.laptop]: {
            marginTop: pxToRem(72),
            marginBottom: pxToRem(83),
            maxWidth: pxToRem(1620),
            fontSize: pxToRem(20),
            lineHeight: 1.3,
        },
    },
    [`& .${accordionContentClassName}  p`]: {
        marginBottom: pxToRem(16),
        color: theme.siteVariables.colorScheme.default?.foreground,
        fontWeight: 400,
        marginTop: 0,
        [theme.screen.laptop]: {
            marginBottom: pxToRem(0),
        },
    },
    [`& .${accordionTitleSlotClassNames.contentWrapper}.${boxClassName}`]: {
        paddingTop: 0,
        paddingRight: 0,
        alignItems: 'flex-start',
        [theme.screen.laptop]: {
            alignItems: 'center',
            paddingTop: pxToRem(38),
            paddingBottom: pxToRem(32),
            paddingRight: pxToRem(20),
        },
    },
    [`& .${accordionTitleSlotClassNames.contentWrapper} > div:first-child`]: {
        marginTop: pxToRem(-1),
        marginRight: pxToRem(10),
    },
    [`& .${accordionTitleSlotClassNames.contentWrapper} > div:nth-child(2)`]: {
        display: 'flex',
        fontWeight: 400,
        letterSpacing: 0,
        fontSize: pxToRem(16),
        lineHeight: '100%',
        height: '100%',
        marginBottom: 0,
        [theme.screen.laptop]: {
            fontSize: pxToRem(36),
            marginBottom: 0,
        },
    },
    [`& .${accordionTitleSlotClassNames.contentWrapper}  svg`]: {
        fill: theme.siteVariables.colorScheme.default?.foreground,
    },
    [`& .${accordionTitleSlotClassNames.contentWrapper} > div:nth-child(2) > div > *`]:
        {
            color: theme.siteVariables.colorScheme.default?.foreground,
            display: 'flex',
            letterSpacing: 0,
            fontSize: pxToRem(16),
            lineHeight: '100%',
            height: '100%',
            marginBottom: 0,
            marginTop: 0,
            fontWeight: 400,
            [theme.screen.laptop]: {
                fontSize: pxToRem(36),
            },
        },
    [`& .${accordionTitleSlotClassNames.contentWrapper} > div:nth-child(2)  p`]:
        {
            color: theme.siteVariables.colorScheme.default?.foreground,
            width: pxToRem(288),
            marginBlockStart: 0,
            marginBlockEnd: 0,
            marginTop: 0,
            marginBottom: 0,
            fontSize: pxToRem(16),
            [theme.screen.laptop]: {
                width: '100%',
                marginBottom: pxToRem(16),
            },
        },
});

export const FaqHTMLContent: StyleRule = ({ theme }) => {
    const headingStyles = {
        ...theme.siteVariables.textStyles.Heading.Medium65,
        fontSize: pxToRem(18),
        lineHeight: '100%',
        marginTop: pxToRem(50),
    };
    const firstChildStyles = {
        marginTop: 0,
    };
    return {
        '& a': {
            color: theme.siteVariables.colorScheme.default?.backgroundActive,
            textDecoration: 'underline',
        },
        '& h2': headingStyles,
        '& h3': headingStyles,
        '& h4': headingStyles,
        '&:first-child:is(h2)': firstChildStyles,
        '&:first-child:is(h3)': firstChildStyles,
        '&:first-child:is(h4)': firstChildStyles,
    };
};

export const IndicatorIcon: StyleRule = () => ({
    transition: 'transform 150ms',
    transform: 'rotate(0deg)',
});

export const IndicatorIconActive: StyleRule = () => ({
    transform: 'rotate(45deg)',
});

export const AccordionTitle: StyleRule = ({ theme }) => ({
    transition: 'background-color 150ms',
    [theme.screen.laptop]: {
        '&:hover': {
            backgroundColor:
                theme.siteVariables.colorScheme.default?.backgroundHover,
        },
    },
});
